import { useNavigate } from '@tanstack/react-router'
import { Command } from 'cmdk'
import {
  ArrowRight,
  BarChart3,
  Building2,
  CalendarClock,
  FilePlus2,
  LayoutDashboard,
  Receipt,
  Repeat,
  Search,
  Settings,
  Sparkles,
  Users,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { InvoiceStatusPill } from '@/components/invoices/InvoiceStatusPill'
import { useCanReadFinance, useIsAdmin } from '@/lib/auth'
import { useClientList } from '@/lib/clients-api'
import { toNumber } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import { useInvoiceList } from '@/lib/invoice-queries'

interface CommandItem {
  id: string
  label: string
  hint?: string
  icon: LucideIcon
  keywords?: string[]
  adminOnly?: boolean
  financeOnly?: boolean
  run: (nav: ReturnType<typeof useNavigate>) => void
}

const groups: { heading: string; items: CommandItem[] }[] = [
  {
    heading: 'Acties',
    items: [
      {
        id: 'new-hours',
        label: 'Nieuwe urenregel',
        hint: 'Uren',
        icon: CalendarClock,
        keywords: ['uren', 'timesheet', 'week', 'regel'],
        run: (nav) => nav({ to: '/uren' }),
      },
      {
        id: 'go-week',
        label: 'Ga naar deze week',
        hint: 'Uren',
        icon: CalendarClock,
        keywords: ['week', 'huidige', 'vandaag'],
        run: (nav) => nav({ to: '/uren' }),
      },
      {
        id: 'team-week',
        label: 'Team-weekoverzicht',
        hint: 'Uren',
        icon: Users,
        keywords: ['team', 'overzicht', 'collega'],
        financeOnly: true,
        run: (nav) => nav({ to: '/uren/team' }),
      },
      {
        id: 'new-invoice',
        label: 'Custom factuur bouwen',
        hint: 'Facturen',
        icon: FilePlus2,
        keywords: ['factuur', 'nieuw', 'invoice'],
        adminOnly: true,
        run: (nav) => nav({ to: '/facturen/nieuw' }),
      },
      {
        id: 'generate-monthly',
        label: 'Maandfacturen genereren',
        hint: 'Wizard',
        icon: Sparkles,
        keywords: ['maand', 'genereer', 'batch'],
        adminOnly: true,
        run: (nav) => nav({ to: '/facturen', search: { wizard: true } }),
      },
    ],
  },
  {
    heading: 'Navigatie',
    items: [
      { id: 'go-dashboard', label: 'Dashboard', icon: LayoutDashboard, run: (nav) => nav({ to: '/' }) },
      { id: 'go-hours', label: 'Uren', icon: CalendarClock, run: (nav) => nav({ to: '/uren' }) },
      { id: 'go-invoices', label: 'Facturen', icon: Receipt, financeOnly: true, run: (nav) => nav({ to: '/facturen' }) },
      { id: 'go-recurring', label: 'Terugkerend', icon: Repeat, financeOnly: true, run: (nav) => nav({ to: '/terugkerend' }) },
      { id: 'go-clients', label: 'Klanten', icon: Building2, financeOnly: true, run: (nav) => nav({ to: '/klanten' }) },
      { id: 'go-reports', label: 'Rapportages', icon: BarChart3, financeOnly: true, run: (nav) => nav({ to: '/rapportages' }) },
      { id: 'go-settings', label: 'Instellingen', icon: Settings, run: (nav) => nav({ to: '/instellingen' }) },
    ],
  },
]

function useDebouncedValue(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

function matchesQuery(item: CommandItem, query: string): boolean {
  if (!query) return true
  const haystack = `${item.label} ${item.keywords?.join(' ') ?? ''}`.toLowerCase()
  return query.toLowerCase().split(/\s+/).every((word) => haystack.includes(word))
}

const itemClass =
  'flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 text-sm text-ink aria-selected:bg-accent-600/10 aria-selected:text-accent-700'
const groupClass =
  'px-1 py-1 text-[11px] font-medium uppercase tracking-wide text-ink-faint **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:pb-1 **:[[cmdk-group-heading]]:pt-2'

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate()
  const isAdmin = useIsAdmin()
  const canReadFinance = useCanReadFinance()
  const [query, setQuery] = useState('')
  const [value, setValue] = useState('')
  const debounced = useDebouncedValue(query.trim(), 150)
  const searching = debounced.length >= 2

  const invoiceResults = useInvoiceList({ status: 'ALL', search: debounced || undefined }, open && searching && canReadFinance)
  const clientResults = useClientList(debounced || undefined, open && searching && canReadFinance)

  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  function close() {
    onOpenChange(false)
  }

  const invoices = searching && canReadFinance ? (invoiceResults.data ?? []).slice(0, 5) : []
  const clients = searching && canReadFinance ? (clientResults.data ?? []).slice(0, 5) : []
  const staticGroups = groups
    .map((group) => ({ ...group, items: group.items.filter((item) => (!item.adminOnly || isAdmin) && (!item.financeOnly || canReadFinance) && matchesQuery(item, debounced)) }))
    .filter((group) => group.items.length > 0)
  const searchPending = searching && (invoiceResults.isLoading || clientResults.isLoading)
  const empty = staticGroups.length === 0 && invoices.length === 0 && clients.length === 0 && !searchPending

  const orderedValues = [
    ...invoices.map((invoice) => `invoice-${invoice.id}`),
    ...clients.map((client) => `client-${client.id}`),
    ...staticGroups.flatMap((group) => group.items.map((item) => item.id)),
  ]
  const firstValue = orderedValues[0] ?? ''

  // biome-ignore lint/correctness/useExhaustiveDependencies: debounced zet de selectie terug bij een nieuwe zoekterm, ook als het eerste resultaat gelijk blijft
  useEffect(() => {
    if (open) setValue(firstValue)
  }, [open, debounced, firstValue])

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      value={value}
      onValueChange={setValue}
      label="Commandopalet"
      shouldFilter={false}
      className="fixed inset-0 z-50"
    >
      <div className="absolute inset-0 bg-(--scrim) backdrop-blur-xs" onClick={close} />
      <div className="pointer-events-none absolute inset-0 flex items-start justify-center pt-[16vh]">
        <div className="glass pointer-events-auto w-[min(92vw,640px)] overflow-hidden rounded-card border border-hairline shadow-pop animate-fade-in">
          <div className="flex items-center gap-2.5 border-b border-hairline px-4">
            <Search className="h-4 w-4 text-ink-faint" />
            <Command.Input
              autoFocus
              value={query}
              onValueChange={setQuery}
              placeholder="Zoek een actie, factuur of klant…"
              className="h-12 flex-1 bg-transparent text-sm text-ink outline-hidden placeholder:text-ink-faint"
            />
            <kbd className="rounded-md border border-hairline bg-fill-2 px-1.5 py-0.5 text-[11px] font-medium text-ink-faint">
              esc
            </kbd>
          </div>
          <Command.List className="max-h-[52vh] overflow-y-auto p-2">
            {empty ? <p className="px-3 py-8 text-center text-sm text-ink-faint">Niets gevonden.</p> : null}

            {invoices.length > 0 ? (
              <Command.Group heading="Facturen" className={groupClass}>
                {invoices.map((invoice) => (
                  <Command.Item
                    key={invoice.id}
                    value={`invoice-${invoice.id}`}
                    onSelect={() => {
                      close()
                      navigate({ to: '/facturen/$id', params: { id: invoice.id } })
                    }}
                    className={itemClass}
                  >
                    <Receipt className="h-[18px] w-[18px] shrink-0 text-ink-faint" strokeWidth={1.85} />
                    <span className="num font-medium normal-case tracking-normal">{invoice.fullNumber ?? 'concept'}</span>
                    <span className="min-w-0 flex-1 truncate normal-case tracking-normal text-ink-soft">
                      {invoice.clientName} · {invoice.subject}
                    </span>
                    <span className="money normal-case text-ink-faint">{formatEuros(toNumber(invoice.total))}</span>
                    <span className="normal-case tracking-normal">
                      <InvoiceStatusPill status={invoice.status} />
                    </span>
                  </Command.Item>
                ))}
              </Command.Group>
            ) : null}

            {clients.length > 0 ? (
              <Command.Group heading="Klanten" className={groupClass}>
                {clients.map((client) => (
                  <Command.Item
                    key={client.id}
                    value={`client-${client.id}`}
                    onSelect={() => {
                      close()
                      navigate({ to: '/klanten/$id', params: { id: client.id } })
                    }}
                    className={itemClass}
                  >
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[10px] font-semibold text-white"
                      style={{ backgroundColor: client.brandColor }}
                    >
                      {client.companyName.slice(0, 2).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1 truncate font-medium normal-case tracking-normal">{client.companyName}</span>
                    <span className="normal-case tracking-normal text-ink-faint">{client.city}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            ) : null}

            {staticGroups.map((group) => (
              <Command.Group key={group.heading} heading={group.heading} className={groupClass}>
                {group.items.map((item) => {
                  const Icon = item.icon
                  return (
                    <Command.Item
                      key={item.id}
                      value={item.id}
                      onSelect={() => {
                        close()
                        item.run(navigate)
                      }}
                      className={itemClass}
                    >
                      <Icon className="h-[18px] w-[18px] text-ink-faint" strokeWidth={1.85} />
                      <span className="flex-1 normal-case tracking-normal">{item.label}</span>
                      {item.hint ? (
                        <span className="text-xs normal-case tracking-normal text-ink-faint">{item.hint}</span>
                      ) : (
                        <ArrowRight className="h-4 w-4 text-ink-faint opacity-0 aria-selected:opacity-100" />
                      )}
                    </Command.Item>
                  )
                })}
              </Command.Group>
            ))}
          </Command.List>
          <div className="flex items-center gap-4 border-t border-hairline px-4 py-2.5 text-[11px] text-ink-faint">
            <Hint keys={['↑', '↓']} label="navigeer" />
            <Hint keys={['↵']} label="open" />
            <Hint keys={['esc']} label="sluit" />
          </div>
        </div>
      </div>
    </Command.Dialog>
  )
}

function Hint({ keys, label }: { keys: string[]; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="flex gap-0.5">
        {keys.map((k) => (
          <kbd key={k} className="inline-flex min-w-4 items-center justify-center rounded-sm border border-hairline bg-fill-2 px-1 py-0.5 font-medium">
            {k}
          </kbd>
        ))}
      </span>
      {label}
    </span>
  )
}
